<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import WhatsappPresenceApi from 'dashboard/api/inbox/whatsapp_presence';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const whatsappPresence = ref(null);
let presenceTimer;

const isWhatsmeowConversation = computed(
  () => inbox.value?.whatsmeow_enabled === true
);

const currentLocale = computed(() =>
  String(locale.value || 'en').replace('_', '-')
);

const parsePresenceTime = value => {
  if (!value) return null;

  if (typeof value === 'number') {
    if (value <= 0) return null;

    const timestamp = value > 1000000000000 ? value : value * 1000;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    const numericValue = Number(value);
    if (numericValue <= 0) return null;

    const timestamp =
      numericValue > 1000000000000 ? numericValue : numericValue * 1000;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const resolvedLastSeenAt = computed(
  () =>
    whatsappPresence.value?.last_seen ||
    whatsappPresence.value?.last_seen_at ||
    whatsappPresence.value?.last_offline_at ||
    whatsappPresence.value?.updated_at ||
    null
);

const hasExplicitLastSeen = computed(() =>
  Boolean(
    whatsappPresence.value?.last_seen || whatsappPresence.value?.last_seen_at
  )
);

const lastSeenText = computed(() => {
  const date = parsePresenceTime(resolvedLastSeenAt.value);
  if (!date) return '';

  return new Intl.DateTimeFormat(currentLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
});

const presenceText = computed(() => {
  if (whatsappPresence.value?.online === true) {
    return t('WHATSAPP_PRESENCE.ONLINE');
  }

  if (lastSeenText.value) {
    return hasExplicitLastSeen.value
      ? t('WHATSAPP_PRESENCE.LAST_SEEN', {
          time: lastSeenText.value,
        })
      : t('WHATSAPP_PRESENCE.LAST_ONLINE_OBSERVED', {
          time: lastSeenText.value,
        });
  }

  return whatsappPresence.value?.available
    ? t('WHATSAPP_PRESENCE.UNAVAILABLE')
    : '';
});

const fetchWhatsappPresence = async () => {
  if (!isWhatsmeowConversation.value || !props.chat?.id) {
    whatsappPresence.value = null;
    return;
  }

  try {
    const { data } = await WhatsappPresenceApi.get(props.chat.id);
    whatsappPresence.value = data;
  } catch {
    whatsappPresence.value = null;
  }
};

const restartPresencePolling = () => {
  if (presenceTimer) clearInterval(presenceTimer);

  if (!isWhatsmeowConversation.value || !props.chat?.id) {
    whatsappPresence.value = null;
    return;
  }

  fetchWhatsappPresence();
  presenceTimer = setInterval(fetchWhatsappPresence, 25000);
};

watch(
  () => [props.chat?.id, isWhatsmeowConversation.value],
  restartPresencePolling
);
onMounted(restartPresencePolling);
onBeforeUnmount(() => clearInterval(presenceTimer));
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex h-[74px] w-full min-w-0 shrink-0 items-center justify-between gap-3 border-b border-lais-border-rule bg-white px-4"
  >
    <div class="flex min-w-0 flex-1 items-center justify-start">
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="40"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="ml-3 flex min-w-0 flex-col items-start overflow-hidden rtl:ml-0 rtl:mr-3"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="truncate text-[15px] font-semibold leading-tight text-lais-text"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="conversation--header--actions flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] text-lais-text-muted"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="presenceText" class="flex items-center gap-1">
            <span
              v-if="whatsappPresence?.online === true"
              class="h-2 w-2 rounded-full bg-n-green-9"
            />
            {{ presenceText }}
          </span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="header-actions-wrap flex shrink-0 flex-row items-center justify-end gap-2"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
